import { Controller } from "@hotwired/stimulus"

// Mention autocomplete for textareas / text inputs in the community feed.
// Detects an @-trigger followed by a partial username, queries the
// workspace member search endpoint with debounce, renders a small floating
// dropdown anchored to the input, and replaces the trigger token with the
// chosen handle on selection. Keyboard: ↑ ↓ Enter (or Tab) to pick, Esc to
// close. Click outside also closes.
export default class extends Controller {
  static targets = ["input"]
  static values  = { url: { type: String, default: "/workspace/mentions/search" } }

  connect() {
    this.menu = null
    this.activeIndex = 0
    this.results = []
    this.triggerStart = -1
    this._debounceTimer = null

    if (!this.hasInputTarget) return
    this.inputTarget.addEventListener("input",   this._onInput   = (e) => this._handleInput(e))
    this.inputTarget.addEventListener("keydown", this._onKeyDown = (e) => this._handleKeydown(e))
    this.inputTarget.addEventListener("blur",    this._onBlur    = () => setTimeout(() => this._closeMenu(), 150))
  }

  disconnect() {
    if (!this.hasInputTarget) return
    this.inputTarget.removeEventListener("input", this._onInput)
    this.inputTarget.removeEventListener("keydown", this._onKeyDown)
    this.inputTarget.removeEventListener("blur", this._onBlur)
    this._closeMenu()
  }

  // ─── Input parsing ───────────────────────────────────────────────────
  _handleInput() {
    const el = this.inputTarget
    const cursor = el.selectionStart
    const upToCursor = el.value.slice(0, cursor)

    // Find the closest "@" before the cursor that starts a token (start of
    // string or preceded by whitespace).
    const match = upToCursor.match(/(^|\s)@([a-zA-Z0-9._-]{0,20})$/)
    if (!match) {
      this._closeMenu()
      return
    }

    const query = match[2]
    this.triggerStart = cursor - query.length - 1 // position of the "@"

    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._fetch(query), 120)
  }

  _handleKeydown(event) {
    if (!this.menu || this.results.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this._move(-1)
        break
      case "Enter":
      case "Tab":
        event.preventDefault()
        this._select(this.results[this.activeIndex])
        break
      case "Escape":
        event.preventDefault()
        this._closeMenu()
        break
    }
  }

  // ─── Fetch ───────────────────────────────────────────────────────────
  async _fetch(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) return this._closeMenu()
      const data = await res.json()
      this.results = Array.isArray(data) ? data : []
      this.activeIndex = 0
      if (this.results.length === 0) {
        this._closeMenu()
      } else {
        this._renderMenu()
      }
    } catch (_) {
      this._closeMenu()
    }
  }

  // ─── Menu rendering ──────────────────────────────────────────────────
  _renderMenu() {
    this._ensureMenu()
    this.menu.replaceChildren()

    this.results.forEach((user, i) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "mention-ac-item"
      if (i === this.activeIndex) item.classList.add("is-active")
      item.dataset.index = String(i)

      const avatar = document.createElement("span")
      avatar.className = "mention-ac-avatar"
      avatar.textContent = user.initial || (user.name || "?").slice(0, 1).toUpperCase()

      const text = document.createElement("span")
      text.className = "mention-ac-text"
      const name = document.createElement("span")
      name.className = "mention-ac-name"
      name.textContent = user.name
      const handle = document.createElement("span")
      handle.className = "mention-ac-handle"
      handle.textContent = `@${user.handle}`
      text.appendChild(name)
      text.appendChild(handle)

      item.appendChild(avatar)
      item.appendChild(text)
      item.addEventListener("mousedown", (e) => {
        // mousedown so it fires before blur closes the menu
        e.preventDefault()
        this._select(user)
      })
      this.menu.appendChild(item)
    })

    this._positionMenu()
  }

  _ensureMenu() {
    if (this.menu) return
    this.menu = document.createElement("div")
    this.menu.className = "mention-ac"
    this.menu.setAttribute("role", "listbox")
    document.body.appendChild(this.menu)
  }

  _positionMenu() {
    if (!this.menu) return
    const rect = this.inputTarget.getBoundingClientRect()
    this.menu.style.position = "fixed"
    this.menu.style.left = `${rect.left}px`
    this.menu.style.top  = `${rect.bottom + 4}px`
    this.menu.style.minWidth = `${Math.min(rect.width, 280)}px`
  }

  _closeMenu() {
    if (this.menu) {
      this.menu.remove()
      this.menu = null
    }
    this.results = []
    this.triggerStart = -1
  }

  _move(delta) {
    if (this.results.length === 0) return
    this.activeIndex = (this.activeIndex + delta + this.results.length) % this.results.length
    this._renderMenu()
  }

  // ─── Selection ───────────────────────────────────────────────────────
  _select(user) {
    if (!user || this.triggerStart < 0) return
    const el = this.inputTarget
    const value = el.value
    const cursor = el.selectionStart
    const before = value.slice(0, this.triggerStart)
    const after  = value.slice(cursor)

    const insertion = `@${user.handle} `
    el.value = before + insertion + after
    const newCursor = before.length + insertion.length
    el.selectionStart = el.selectionEnd = newCursor
    el.focus()
    this._closeMenu()

    // Fire input so any other listeners (counter, validators) re-evaluate.
    el.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
